import { Controller } from "react-hook-form";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { Form } from "../../../../../layout/FormLayout";
import type { FormSource } from "./CreatePimProduct.utils";
import { InputWrapper } from "./CreatePimProduct.utils";
import {
  H5,
  SoftHeader2,
  H4,
} from "../../../../../components/Typography/Typography";
import { useTranslation } from "react-i18next";
import { z as zod } from "zod";
import {
  zodOptionalSelectBoxDefault,
  zodOptionalString,
  zodRequiredString,
} from "../../../../../util/zod.util";
import { useFormWrapper, useStoreState } from "../../../../../util/util";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import type {
  AttributeTemplateSummarySchema,
  PIMProductBase,
  ProductCreationRequest,
  TemplateProductFormSchema,
} from "../../../../../types/types.PIM";
import type { AxiosError } from "axios";
import axios from "axios";
import { endpoints } from "../../../../../endpoints";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { useTheme } from "styled-components/macro";
import { OptionType, User, WithPagination } from "../../../../../types/types";
import { WhiteInnerCard } from "../../../../../layout/portalPageLayout";
import { strings } from "../../../../../util/strings";
import { Modal } from "../../../../../components/Modal/Modal";
import useSWR from "swr";
import ReactTooltip from "react-tooltip";
import { widthTokens } from "../../../../../designTokens";

export const CreateNewProductViaFormPage = ({
  formSource,
  goToTabByKey,
}: {
  formSource: FormSource;
  goToTabByKey: (key: string) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [recipientType] = useState<"internal" | "external">("internal");

  const { tenant_id, storefront_id } = useStoreState();

  const { t } = useTranslation();
  const { notifyError } = useNotifications();
  const theme = useTheme();

  const { data: templates } = useSWR<
    WithPagination<{ data: AttributeTemplateSummarySchema[] }>
  >(endpoints.v2_storefronts_id_pim_templates_summary(tenant_id));
  const templatesOptions = templates?.data?.reduce((prev, current) => {
    if (current.enable_custom_forms) {
      return [
        ...prev,
        { value: current.template_id, label: current.template_name },
      ];
    }
    return prev;
  }, [] as OptionType<string>[]);

  const PimNewProductFormSchema = zod.object({
    brand_name: zodRequiredString(t),
    product_template: zod
      .object(
        {
          value: zod
            .object({
              id: zod.string().min(1, strings(t).thisIsARequiredField),
              forms: zod.array(
                zod.object({
                  id: zod.string(),
                  name: zod.string(),
                })
              ),
            })
            .required(),
          label: zodRequiredString(t).nullable(),
        },
        { required_error: strings(t).thisIsARequiredField }
      )
      .required(),
    template_form: zod
      .object(
        {
          value: zod.string().min(1, strings(t).thisIsARequiredField),
          label: zod.string().min(1, strings(t).thisIsARequiredField),
        },
        { required_error: strings(t).thisIsARequiredField }
      )
      .required(),
    // recipient_type: zod.enum(["internal", "external"], {
    //   errorMap: () => ({ message: strings(t).thisIsARequiredField }),
    // }),
    internal_users:
      recipientType === "internal"
        ? zod
            .object(
              {
                value: zod.string().min(1, strings(t).thisIsARequiredField),
                label: zod.string().min(1, strings(t).thisIsARequiredField),
              },
              { required_error: strings(t).thisIsARequiredField }
            )
            .required()
        : zodOptionalSelectBoxDefault.optional().nullable(),
    name:
      recipientType === "external" ? zodRequiredString(t) : zodOptionalString,
    email:
      recipientType === "external"
        ? zod
            .string()
            .min(1, strings(t).thisIsARequiredField)
            .email(strings(t).emailAddressMustBeValid)
            .nullable()
        : zodOptionalString,
    message: zodOptionalString.nullable(),
  });

  type FormInputs = zod.infer<typeof PimNewProductFormSchema>;

  const { handleSubmit, register, formState, errors, control, watch, reset } =
    useFormWrapper<FormInputs>({
      resolver: zodResolver(PimNewProductFormSchema),
      defaultValues: {},
    });

  const selectedTemplate = watch("product_template");
  const selectedForm = watch("template_form");

  const onSubmit = (data: FormInputs) => {
    setLoading(true);

    const url =
      endpoints.v2_storefronts_id_pim_product_creation_form(storefront_id);

    const requestBody: ProductCreationRequest = {
      template_id: selectedTemplate?.value.id,
      form_id: selectedForm?.value,
      product_name: data.brand_name.trim(),
      recipient_type: "internal",
      user_id: data.internal_users?.value,
      ...(data.message && {
        message: data.message,
      }),
    };

    axios
      .post<PIMProductBase>(url, requestBody)
      .then(() => {
        setShowSuccessModal(true);
        setLoading(false);
      })
      .catch((error: AxiosError) => {
        notifyError(
          error.response
            ? error.response.data.message
            : t(`There was an error creating the product, please try again`),
          { error }
        );
        setLoading(false);
      });
  };
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    goToTabByKey("request_tracking");
  };

  useEffect(() => {
    if (formSource === "new") {
      reset();
    }
  }, [formSource, reset]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <H5 style={{ marginTop: "25px", marginBottom: 0 }}>
        {t("Product Information")}
      </H5>
      <InputWrapper style={{ marginTop: 0, marginBottom: 0 }}>
        <div
          data-testid="product-template-select"
          data-for="product-template-select"
          data-tip={
            templatesOptions?.length === 0
              ? t(
                  "No template has a form configured. Please create a new product creation form in a template"
                )
              : undefined
          }
        >
          <Controller
            as={SearchSelectInfiniteScroll}
            control={control}
            name="product_template"
            placeholder={t("Template")}
            id="product_template"
            baseUrl={endpoints.v2_storefronts_id_pim_templates_summary(
              tenant_id
            )}
            params={
              new URLSearchParams({
                order_by: "asc",
                template_type: "product",
              })
            }
            getOptions={(response: AttributeTemplateSummarySchema[]) =>
              response.reduce(
                (
                  prev: {
                    value: { id: string; forms: TemplateProductFormSchema[] };
                    label: string;
                  }[],
                  { template_name, template_id, enable_custom_forms, forms }
                ) => {
                  if (enable_custom_forms) {
                    return [
                      ...prev,
                      {
                        value: {
                          id: template_id,
                          forms: forms ?? [],
                        },
                        label: template_name,
                      },
                    ];
                  }
                  return prev;
                },
                []
              )
            }
            disabled={templatesOptions?.length === 0}
            defaultValue={undefined}
            errors={errors}
            formState={formState}
          />
        </div>
        <ReactTooltip id="product-template-select" />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="template_form"
          placeholder={t("Form")}
          id="template_form"
          disabled={!selectedTemplate}
          options={
            selectedTemplate?.value?.forms?.length &&
            selectedTemplate.value.forms.length > 0
              ? selectedTemplate.value.forms.map((form) => ({
                  label: form.name,
                  value: form.id,
                }))
              : []
          }
          defaultValue={{ value: "", label: "" }}
          errors={errors}
          formState={formState}
        />
        <TextField
          name="brand_name"
          label={t("Product Name")}
          theref={register({
            required: true,
          })}
          formState={formState}
          errors={errors}
          type="text"
        />

        <H5 style={{ marginBottom: 0, marginTop: theme.resolve("spacing_xs") }}>
          {t("Recipient Type")}
        </H5>
        <SoftHeader2 style={{ marginBottom: theme.resolve("spacing_md") }}>
          {t("Who would fill out the information?")}
        </SoftHeader2>
        {/* <InputWrapper style={{ marginTop: 0, marginBottom: 0, padding: 0 }}>
          <RadioButtonContainer
            style={{
              flexDirection: "column",
              marginBottom: theme.resolve("spacing_lg"),
            }}
          >
            <div style={{ marginBottom: theme.resolve("spacing_md") }}>
              <RadioButton
                name="recipient_type"
                value="internal"
                optionTitle={t(`Internal`)}
                theref={register({
                  required: true,
                })}
                fontSize={theme.fontSizes.medium}
                width={14}
                height={14}
                color={
                  !recipientType && errors.recipient_type
                    ? `var(--color_error_dark)`
                    : recipientType === "internal"
                    ? theme.colors.darkText
                    : theme.colors.grayText
                }
                handleChange={() => setRecipientType("internal")}
                checked={recipientType === "internal"}
                style={{ marginBottom: theme.resolve("spacing_md") }}
              />
            </div>
            <div style={{ marginBottom: theme.resolve("spacing_sm") }}>
              <RadioButton
                name="recipient_type"
                value="external"
                optionTitle={t(`External`)}
                theref={register({
                  required: true,
                })}
                fontSize={theme.fontSizes.medium}
                width={14}
                height={14}
                color={
                  !recipientType && errors.recipient_type
                    ? `var(--color_error_dark)`
                    : recipientType === "external"
                    ? theme.colors.darkText
                    : theme.colors.grayText
                }
                handleChange={() => setRecipientType("external")}
                checked={recipientType === "external"}
                style={{ marginBottom: theme.resolve("spacing_md") }}
              />
            </div>
            {errors.recipient_type && (
              <FormInputErrorText className="errorMessage">
                {strings(t).thisIsARequiredField}
              </FormInputErrorText>
            )}
          </RadioButtonContainer>
        </InputWrapper> */}
        {(recipientType === "internal" || recipientType === "external") && (
          <WhiteInnerCard
            style={{
              padding: `${theme.resolve("spacing_2xl")} ${theme.resolve(
                "spacing_2xl"
              )} ${theme.resolve("spacing_md")}  ${theme.resolve(
                "spacing_2xl"
              )}`,
              marginBottom: 0,
            }}
          >
            <H5 style={{ marginBottom: theme.resolve("spacing_lg") }}>
              {recipientType === "internal" ? t("Internal") : t("External")}
            </H5>
            <InputWrapper style={{ marginTop: 0, marginBottom: 0, padding: 0 }}>
              {recipientType === "internal" && (
                <Controller
                  as={SearchSelectInfiniteScroll}
                  control={control}
                  name="internal_users"
                  theref={register({
                    required: false,
                  })}
                  errors={errors}
                  isClearable={true}
                  defaultValue={undefined}
                  formState={formState}
                  placeholder={t("Select User")}
                  baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                    storefront_id,
                    tenant_id
                  )}
                  params={(() => {
                    const params = new URLSearchParams();
                    params.append("order_by", "asc");
                    params.append("is_active", "true");
                    return params;
                  })()}
                  getOptions={(response: User[]) =>
                    response.map((user) => ({
                      value: user.id,
                      label: `${user.firstname} ${user.lastname}`,
                    }))
                  }
                />
              )}
              {recipientType === "external" && (
                <>
                  <TextField
                    name="name"
                    label={t("Name")}
                    theref={register({
                      required: false,
                    })}
                    formState={formState}
                    errors={errors}
                    type="text"
                  />
                  <TextField
                    name="email"
                    label={t("Email")}
                    formState={formState}
                    theref={register({
                      required: false,
                    })}
                    errors={errors}
                    type="text"
                  />
                </>
              )}
              <TextField
                name="message"
                label={t("Message (Optional)")}
                theref={register({
                  required: false,
                })}
                formState={formState}
                errors={errors}
                type="text"
              />
            </InputWrapper>
          </WhiteInnerCard>
        )}
      </InputWrapper>
      <ButtonContainer>
        <PrimaryButtonLargeV2 loading={loading}>
          {t("Create")}
        </PrimaryButtonLargeV2>
      </ButtonContainer>

      <Modal
        show={showSuccessModal}
        overlay={true}
        closeModal={handleCloseSuccessModal}
        modalWidth={widthTokens.width_sm}
      >
        <H4
          style={{
            marginBottom: theme.resolve("spacing_3xl"),
            marginTop: theme.resolve("spacing_sm"),
          }}
        >
          {t("Request Status")}
        </H4>
        <WhiteInnerCard
          style={{
            marginBottom: theme.resolve("spacing_3xl"),
            backgroundColor: theme.resolve("color_brand_3"),
            borderColor: theme.resolve("color_brand_primary"),
          }}
        >
          {t(
            "Your request has been successfully sent. Both you and the recipient will receive an email with a link to the submission form. You will be notified again once the recipient submits the requested data."
          )}
        </WhiteInnerCard>
        <TertiaryButtonLargeV2 onClick={handleCloseSuccessModal} fullWidth>
          {t("Close")}
        </TertiaryButtonLargeV2>
      </Modal>
    </Form>
  );
};
